import { useId } from 'react'
import type { Ref } from 'react'
import { RadioGroup } from '../../components/ui/Radio'
import { Textarea } from '../../components/ui/Textarea'
import type { components } from '../../lib/api/schema.gen'
import { REGION_OPTIONS } from './review-domain'

type RfqRegion = components['schemas']['RfqRegion']

const NOTES_MAX_LENGTH = 2000

export interface TargetingCardProps {
  readonly needByDate: string
  readonly onNeedByDateChange: (value: string) => void
  readonly needByDateError?: string | undefined
  readonly needByDateInputRef?: Ref<HTMLInputElement> | undefined
  readonly region: RfqRegion
  readonly onRegionChange: (value: RfqRegion) => void
  readonly notes: string
  readonly onNotesChange: (value: string) => void
}

/**
 * RFQ review screen's targeting block (screens.md §15): need-by date, the
 * bounded region radio list (ADR-0070's `RfqRegion` US sourcing-preference
 * enum — `usa` / `prefer_usa` / `any`, `any` default-selected; there is no
 * longer a client-only "no preference" sentinel, `any` is a real member),
 * and the notes textarea (contract-bounded to 2000 chars — `maxLength` here
 * is the belt to the server's braces).
 *
 * UID-359 (items 1/4) — NO LONGER A CARD, and no longer carries a "Targeting"
 * heading. The design's expanded "Request details & services" body is ONE flat
 * region (`Fictiv Platform.dc.html:4254`, `padding:2px 20px 20px` + an inset
 * top hairline), not a card-in-a-card: this block and `AddOnServicesCard` are
 * two halves of that single region, separated only by a 1px rule (`:4272`).
 * Nesting them in `<Card>`s stacked two sets of 16px padding and two borders
 * inside the parent card, which is most of why the built form read as so much
 * taller and boxier than the comp.
 *
 * The design's card heading is replaced by its own lead-in line (`:4255`,
 * "What are you targeting?" + a muted "Optional — helps us quote the right
 * way").
 *
 * UID-359 Gate-5 (WCAG 1.3.1) — that lead-in was built as a plain `<p>`, on
 * the reasoning that the parent disclosure header already labels the
 * section. But the disclosure header is a `<button>`, not a heading
 * (`RequestDetailsSection.tsx` — out of this fix's scope to change), so a
 * screen-reader user navigating by heading had no way to land on this block
 * at all: it and `AddOnServicesCard`'s label were the page's only two
 * regions with no heading in the outline. The lead-in is now an `<h3
 * className="rfq-targeting-prompt">` (same class — `.rfq-targeting-prompt`
 * already sets its own explicit `margin`/`font-size`/`font-weight`, and
 * `global.css`'s app-wide `h1..h6 { margin: 0; font-family: var(--font-
 * family-heading) }` reset both defers to that explicit margin via
 * specificity and resolves to the same Museo Sans the body already uses
 * post-ADR-0094 — so promoting the tag changes nothing visually), and the
 * wrapping block restores a programmatic name via `aria-labelledby`, the
 * same convention `AddOnServicesCard` restores below it. `<h3>` for the
 * same reason as that sibling block: nested inside the page's collapsible
 * "Request details & services" section, which occupies this page's h2 slot
 * conceptually (alongside "Parts in this request"/"Request summary")
 * despite being a `<button>`, not a literal heading.
 */
export function TargetingCard({
  needByDate,
  onNeedByDateChange,
  needByDateError,
  needByDateInputRef,
  region,
  onRegionChange,
  notes,
  onNotesChange,
}: TargetingCardProps) {
  const dateId = useId()
  const dateErrorId = useId()
  const targetingHeadingId = useId()

  return (
    <div className="rfq-targeting-block" aria-labelledby={targetingHeadingId}>
      {/* `dc.html:4255` — 12px/700 secondary lead-in with a lighter, smaller
       * "Optional" qualifier inline. UID-359 Gate-5 — a real `<h3>` (not a
       * `<p>`), so this region has a heading reachable by screen-reader
       * heading navigation again; same class, so still 12px/700 (see doc
       * comment above). */}
      <h3 id={targetingHeadingId} className="rfq-targeting-prompt">
        What are you targeting?{' '}
        <span className="rfq-targeting-prompt-optional">
          Optional — helps us quote the right way
        </span>
      </h3>

      {/* `dc.html:4256` — Need-by date and Region requirement sit SIDE BY SIDE
       * (`display:flex;gap:12px;flex-wrap:wrap`, each `flex:1;min-width:180px`),
       * not stacked. The wrap keeps them stacking naturally below ~380px of
       * available width without a media query. */}
      <div className="rfq-targeting-row">
        <div className="form-field rfq-targeting-col">
          <label htmlFor={dateId} className="form-field-label">
            Need-by date
          </label>
          <input
            ref={needByDateInputRef}
            id={dateId}
            type="date"
            value={needByDate}
            onChange={(event) => onNeedByDateChange(event.target.value)}
            aria-invalid={needByDateError ? true : undefined}
            aria-describedby={needByDateError ? dateErrorId : undefined}
            className="form-field-input"
          />
          {needByDateError && (
            <p id={dateErrorId} className="form-field-error">
              {needByDateError}
            </p>
          )}
        </div>

        {/* Legend copy is the design's exact string (design_handoff_misumi_unified_platform/
            "Fictiv Platform (standalone).html" — the label immediately preceding
            `rfqDraftView.regionOpts`), not the placeholder "Region" this card used
            before ADR-0070. */}
        <div className="rfq-targeting-col">
          <RadioGroup
            name="rfq-region"
            legend="Region requirement"
            options={REGION_OPTIONS}
            value={region}
            onChange={onRegionChange}
          />
        </div>
      </div>

      <Textarea
        label="Notes for sourcing"
        value={notes}
        maxLength={NOTES_MAX_LENGTH}
        rows={4}
        placeholder="Tolerances, cosmetic requirements, target volumes, anything else we should know…"
        onChange={(event) => onNotesChange(event.target.value)}
      />
    </div>
  )
}
